import _ from "lodash";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { UserRecord } from "src/types/main";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { twMerge } from "tailwind-merge";
import { EmptyList } from "../EmptyList";
import SortIcon from "../ui-icons/table/SortIcon";
import { TableHeaderColumnProps } from "../ui/table/Table";
import {
  TGUserAvatarsList2,
  UserAvatarItem,
  UserAvatarType2,
} from "../ui/TGUserAvatarsList";
import Loading from "../Loading";

export type AuditReportTableType = {
  ID: number;
  Document: {
    Code: string;
    Name: string;
  };
  Author: UserRecord;
  PublicationDate: string;
  AuditEmployees: UserAvatarType2[];
  AuditPercentage: number;
  AverageTestResult: number | null;
  Version: number;
};

const tableHeaderContent = {
  Document: {
    title: "Код документа",
    sort: true,
    style: { width: "15%" },
  },
  Version: {
    title: "Версия документа",
    sort: false,
    style: { width: "10%" },
  },
  Author: {
    title: "Владелец документа",
    sort: true,
    style: { width: "15%" },
  },
  PublicationDate: {
    title: "Дата публикации",
    sort: true,
    style: { width: "15%" },
  },
  AuditEmployees: {
    title: "Ознакомившиеся сотрудники",
    sort: false,
    style: { width: "25%" },
  },
  AuditPercentage: {
    title: "Процент ознакомления",
    sort: true,
    style: { width: "10%" },
  },
  AverageTestResult: {
    title: "Средний результат тестирования",
    sort: true,
    style: { width: "10%" },
  },
};

function AuditReportTable({
  body,
  loading,
}: {
  body: AuditReportTableType[];
  loading: boolean;
}) {
  return <Table loading={loading} header={tableHeaderContent} body={body} />;
}

export default AuditReportTable;

function Table({
  header,
  body,

  loading,
}: {
  header: typeof tableHeaderContent;
  body: AuditReportTableType[];

  loading: boolean;
}) {
  const [sortedField, setSortedField] = useState<
    keyof AuditReportTableType | null
  >(null);
  const [sortDirection, setSortDirection] = useState<
    "asc" | "desc" | "default"
  >("default");

  const handleSort = (field: keyof typeof tableHeaderContent) => {
    if (sortedField === field) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc"
          ? "desc"
          : prevDirection === "desc"
          ? "default"
          : "asc"
      );
    } else {
      if (field) setSortedField(field);
      setSortDirection("asc");
    }
  };

  const sortedTableBodyContent = useMemo(() => {
    if (!sortedField || sortDirection === "default") {
      return body;
    }

    const sortedData = [...body];

    sortedData.sort((a, b) => {
      if (sortedField === "Document") {
        return sortDirection === "asc"
          ? a[sortedField].Name.localeCompare(b[sortedField].Name)
          : b[sortedField].Name.localeCompare(a[sortedField].Name);
      }

      if (sortedField === "Author") {
        return sortDirection === "asc"
          ? a[sortedField].Title.localeCompare(b[sortedField].Title)
          : b[sortedField].Title.localeCompare(a[sortedField].Title);
      }

      const valueA = a[sortedField];
      const valueB = b[sortedField];

      if (sortedField === "AverageTestResult") {
        if (_.isNumber(valueA) && _.isNumber(valueB)) {
          if (sortDirection === "asc") {
            return valueA - valueB;
          } else {
            return valueB - valueA;
          }
        }

        // Если одно из значений `null`, оно должно быть после числа при сортировке по возрастанию и до числа при сортировке по убыванию
        if (_.isNumber(valueA) && valueB === null) {
          return sortDirection === "asc" ? -1 : 1;
        } else if (valueA === null && _.isNumber(valueB)) {
          return sortDirection === "asc" ? 1 : -1;
        }

        // Если оба значения `null` или оба не числа
        return 0;
      }

      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortDirection === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      } else if (typeof valueA === "boolean" && typeof valueB === "boolean") {
        return sortDirection === "asc"
          ? Number(valueA) - Number(valueB)
          : Number(valueB) - Number(valueA);
      } else if (typeof valueA === "number" && typeof valueB === "number") {
        return sortDirection === "asc" ? valueA - valueB : valueB - valueA;
      }

      return 0;
    });

    return sortedData;
  }, [sortedField, sortDirection, body]) as AuditReportTableType[];

  return (
    <FlexCol className="w-full">
      <TableList
        head={header}
        body={sortedTableBodyContent}
        sort={handleSort}
        sortDirection={sortDirection}
        sortedField={sortedField}
        loading={loading}
      />
    </FlexCol>
  );
}

function TableList({
  head,
  body,
  sort,
  sortDirection,
  sortedField,
  loading,
}: {
  head: typeof tableHeaderContent;
  body: AuditReportTableType[];
  sort: (f: keyof typeof tableHeaderContent) => void;
  sortedField: keyof AuditReportTableType | null;
  sortDirection: "asc" | "desc" | "default";
  loading: boolean;
}) {
  const [query, setQuery] = useSearchParams();

  function openDocumentCard(id: number) {
    const newSearchParams = new URLSearchParams(query);

    newSearchParams.set("documentCard", id.toString());

    setQuery(newSearchParams);
  }

  return (
    <FlexRow className="px-6 py-5 overflow-x-auto bg-white rounded-lg table-custom shadow-custom scrollbar scrollbar-thumb-rounded-lg scrollbar-h-2 scrollbar-thumb-[#D1D5DB]">
      <FlexCol className="divide-y min-w-[1235px] grow">
        <FlexRow className="pb-5 gap-x-4">
          {Object.keys(head).map((fieldKey) => {
            const key = fieldKey as keyof typeof tableHeaderContent;

            return (
              <TableHeaderColumn
                key={fieldKey}
                tableHeaderContent={head[key]}
                sortDirection={sortedField === key ? sortDirection : "default"}
                onClick={() => sort(key)}
              />
            );
          })}
        </FlexRow>
        {loading ? (
          <div className="py-4">
            <Loading />
          </div>
        ) : Array.isArray(body) && body.length > 0 ? (
          body.map((item, index) => (
            <FlexRow
              className="py-4  hover:bg-[#F3F4F6] hover:bg-opacity-50 rounded-lg  cursor-pointer gap-x-4  -mx-2 px-2"
              key={index + 1}
              onClick={() => openDocumentCard(item.ID)}
            >
              <FlexCol
                className="break-words text-[#637381]"
                style={{ width: "15%" }}
              >
                <div>{item.Document.Code}</div>
                <div className="text-darkblue">{item.Document.Name}</div>
              </FlexCol>
              <FlexCol
                className="break-words text-darkblue"
                style={{ width: "10%" }}
              >
                {item.Version}
              </FlexCol>
              <FlexCol
                className="break-words text-darkblue"
                style={{ width: "15%" }}
              >
                <UserAvatarItem
                  item={{
                    name: item.Author.Name,
                    shortName: "",
                    email: item.Author.Email,
                  }}
                  single={true}
                  classImg="hidden"
                />
              </FlexCol>
              <FlexCol
                className="break-words text-[#637381]"
                style={{ width: "15%" }}
              >
                {item.PublicationDate}
              </FlexCol>
              <FlexCol
                className="break-words text-[#637381] grow"
                style={{ width: "25%" }}
              >
                <TableItemEmployees employees={item.AuditEmployees} />
              </FlexCol>
              <FlexCol
                className="break-words text-[#637381]"
                style={{ width: "10%" }}
              >
                {item.AuditPercentage}%
              </FlexCol>
              <FlexCol
                className="break-words text-[#637381]"
                style={{ width: "10%" }}
              >
                {item.AverageTestResult !== null
                  ? item.AverageTestResult + "%"
                  : "----"}
              </FlexCol>
            </FlexRow>
          ))
        ) : (
          <EmptyList title="Здесь будут храниться все уведомления" />
        )}
      </FlexCol>
    </FlexRow>
  );
}

const renderAvatars = (
  containerWidth: number,
  employees: UserAvatarType2[]
) => {
  const countItems = Math.ceil(containerWidth / 26);
  const remainingCount = employees.length - countItems + 1;

  const slidedItems = employees
    .sort(
      (a, b) => Number(b.status === "success") - Number(a.status === "success")
    )
    .slice(0, countItems - 1);

  return (
    <TGUserAvatarsList2
      items={slidedItems}
      classImg="w-7.5 h-7.5"
      last={
        countItems >= employees.length ? null : (
          <div
            className={twMerge(
              employees.length > 100 ? "bg-[#BA0C2F]" : "bg-gray-500",
              "flex -translate-x-1 shrink-0 w-7.5 h-7.5 rounded-full ring-white ring-1 items-center justify-center text-white text-[10px]"
            )}
          >
            +{remainingCount >= 999 ? 999 : remainingCount}
          </div>
        )
      }
    />
  );
};

function TableItemEmployees({ employees }: { employees: UserAvatarType2[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [avatar, setAvatar] = useState<ReactNode | null>(null);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const node = renderAvatars(containerWidth, employees);
        setAvatar(node);
      }
    };

    if (containerRef.current) {
      handleResize();
    }
  }, [JSON.stringify(employees)]);

  return (
    <FlexCol className="break-words grow">
      <div id="avatar-container" ref={containerRef} className="w-full">
        {employees.length > 0 && avatar}
      </div>
    </FlexCol>
  );
}

export function TableHeaderColumn({
  tableHeaderContent,
  sortDirection,
  onClick,
}: TableHeaderColumnProps) {
  return (
    <FlexCol className="select-none" style={tableHeaderContent.style}>
      <FlexRow className="items-center gap-x-2">
        {tableHeaderContent.icon && (
          <FlexCol>{tableHeaderContent.icon}</FlexCol>
        )}
        <FlexCol className="font-medium whitespace-pre-line w-fit">
          {tableHeaderContent.title}
        </FlexCol>
        {tableHeaderContent.sort && (
          <FlexCol className="w-auto cursor-pointer" onClick={onClick}>
            <SortIcon sortDirection={sortDirection} />
          </FlexCol>
        )}
      </FlexRow>
    </FlexCol>
  );
}
